import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { PAGE_LAYOUT } from "../config/page.static";
import { PageType } from "../config/page.types";

// Breadcrumb provider
// Manages navigation history as a stack
// Keeps sync with router and localStorage
// Supports dynamic labels for detail pages

export type BreadcrumbItem = {
	pageType: PageType;
	icon?: React.ElementType;
	label: string;
	path?: string;
	isLabelOnly?: boolean;
	dynamicLabel?: string;
};

type BreadcrumbContextType = {
	breadcrumbs: BreadcrumbItem[];
	visitPage: (page: PageType, fromMenu?: boolean, dynamicLabel?: string, id?: string) => void;
	goToBreadcrumb: (page: PageType) => void;
};

const STORAGE_KEY = "app:breadcrumbs";
const BreadcrumbContext = createContext<BreadcrumbContextType | null>(null);

// Clears persisted breadcrumbs from localStorage.
// Call on logout / workspace change so the next session doesn't restore stale breadcrumbs.
export const clearBreadcrumbsStorage = () => {
	try {
		localStorage.removeItem(STORAGE_KEY);
	} catch {
		// silent fail
	}
};

export const BreadcrumbProvider = ({ children }: { children: ReactNode }) => {
	const location = useLocation();

	// Navigation stack
	const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);

	// Prevent route sync before initial hydration
	const [hydrated, setHydrated] = useState(false);

	// Normalize path to avoid trailing slash mismatches
	const normalize = (p?: string) => (p ? p.replace(/\/$/, "") : "");

	const matchPath = (configuredPath: string, pathname: string) => {
		const pattern = configuredPath.replace(/:[^/]+/g, "[^/]+").replace(/\//g, "\\/");

		return new RegExp(`^${pattern}$`).test(pathname);
	};

	const resolvePage = useCallback((pathname: string): PageType | null => {
		const normalizedPath = normalize(pathname);

		const entry = Object.entries(PAGE_LAYOUT).find(([_, cfg]) =>
			cfg.breadcrumbs?.some((breadcrumb) => {
				if (!breadcrumb.path) return false;

				return matchPath(normalize(breadcrumb.path), normalizedPath);
			})
		);

		return entry ? (entry[0] as PageType) : null;
	}, []);

	// Hydrate state from localStorage or fallback to URL
	useEffect(() => {
		const stored = localStorage.getItem(STORAGE_KEY);

		if (stored) {
			try {
				const parsed: BreadcrumbItem[] = JSON.parse(stored);

				const currentPath = normalize(location.pathname);

				const lastStored = parsed[parsed.length - 1];
				const lastStoredPath = normalize(lastStored?.path);

				// check consistency between stored state and current URL
				const isSameRoute = lastStoredPath === currentPath;

				if (isSameRoute) {
					// restore icons
					const restored = parsed.map((item) => {
						const cfg = PAGE_LAYOUT[item.pageType]?.breadcrumbs;
						const match = cfg?.find((b) => b.label === item.label);

						return {
							...item,
							icon: match?.icon,
						};
					});

					setBreadcrumbs(restored);
					setHydrated(true);
					return;
				}

				// ❗ fallback if not same route
				localStorage.removeItem(STORAGE_KEY);
			} catch {
				localStorage.removeItem(STORAGE_KEY);
			}
		}

		// fallback → build from URL
		const page = resolvePage(location.pathname);

		if (page) {
			const first = PAGE_LAYOUT[page]?.breadcrumbs || [];

			setBreadcrumbs(
				first.map((b) => ({
					...b,
					pageType: page,
					dynamicLabel: undefined,
				}))
			);
		}

		setHydrated(true);
	}, []);

	// Persist breadcrumb state
	useEffect(() => {
		if (breadcrumbs.length) {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(breadcrumbs));
		}
	}, [breadcrumbs]);

	// Sync breadcrumb stack with route changes
	useEffect(() => {
		if (!hydrated) return;

		const path = normalize(location.pathname);

		setBreadcrumbs((current) => {
			// Check if current route already exists in stack
			const index = current.findIndex((c) => normalize(c.path) === path);

			// If exists, trim forward history
			if (index >= 0) {
				return current.slice(0, index + 1);
			}

			const page = resolvePage(location.pathname);
			if (!page) return current;

			const configuration = PAGE_LAYOUT[page]?.breadcrumbs;
			if (!configuration?.length) return current;

			const last = configuration[configuration.length - 1];

			// Add new breadcrumb entry
			const next: BreadcrumbItem = {
				...last,
				pageType: page,
				path: last.path,
				dynamicLabel: undefined,
			};

			return [...current, next];
		});
	}, [location.pathname, hydrated]);

	// Public navigation API
	const visitPage = useCallback((page: PageType, fromMenu = false, dynamicLabel?: string, id?: string) => {
		const configuration = PAGE_LAYOUT[page]?.breadcrumbs;
		if (!configuration) return;

		setBreadcrumbs((current) => {
			// Reset entire stack when navigation comes from menu
			if (fromMenu) {
				return configuration.map((b, i) => ({
					...b,
					pageType: page,
					// Use dynamic label only for the last breadcrumb
					dynamicLabel: i === configuration.length - 1 ? dynamicLabel : undefined,
				}));
			}

			// Clone current stack to avoid mutation
			const stack = [...current];

			// If we are navigating to a detail page with an id,
			// remove any transient "add-unit" breadcrumb
			if (id) {
				const cleaned = stack.filter(
					(b) => b.pageType !== PageType.MasterDataOperationalUnitsAddUnit
				);

				// Replace stack content (preserve reference)
				stack.length = 0;
				stack.push(...cleaned);
			}

			// Add missing breadcrumbs based on page configuration
			for (const breadcrumbs of configuration) {
				// Replace dynamic :id in path if present
				const path =
					id && breadcrumbs.path?.includes(":id") ? breadcrumbs.path.replace(":id", id) : breadcrumbs.path;

				// Skip invalid paths
				if (!path) continue;

				// Avoid duplicates (exact path match)
				const exists = stack.some((x) => x.path === path);

				if (!exists) {
					stack.push({
						...breadcrumbs,
						path,
						pageType: page,
						// Use dynamic label only for detail pages
						dynamicLabel: breadcrumbs.path?.includes(":id") ? dynamicLabel : undefined,
					});
				}
			}

			return stack;
		});
	}, []);

	// Navigate back to breadcrumb
	const goToBreadcrumb = useCallback((page: PageType) => {
		setBreadcrumbs((current) => {
			const index = current.findIndex((c) => c.pageType === page);
			return index >= 0 ? current.slice(0, index + 1) : current;
		});
	}, []);

	return (
		<BreadcrumbContext.Provider value={{ breadcrumbs, visitPage, goToBreadcrumb }}>
			{children}
		</BreadcrumbContext.Provider>
	);
};

export const useBreadcrumbs = () => {
	const ctx = useContext(BreadcrumbContext);

	if (!ctx) {
		throw new Error("useBreadcrumbs must be inside provider");
	}

	return ctx;
};
